import React, { useState, useEffect, FC } from 'react';
import Head from 'next/head';
import axios from 'axios';
import querystring from 'query-string';

import LayoutPage from 'src/modules/layout/components/page/LayoutPage';
import { useIntl } from 'react-intl';
import { Row, Col } from 'antd';
import { rowDefaultProps } from 'src/utils/grid';
import { NewsletterPageTypes } from 'src/modules/pages/components/newsletter/PagesNewsletter';
import { onFinishGlobalNewsletter } from 'src/modules/global';
import { GlobalFormNewsletter, GlobalButton, getNewsletterValues } from 'src/modules/global';
import { PagesNewsletter } from 'src/modules/pages';
import { getCurrentUrlParams } from 'src/utils/url';
import { useLocale, OutputChannels } from 'src/modules/locale';
import styles from './PagesNewsletterPreferences.module.scss';

interface PagesNewsletterPreferencesProps {
  variant?: 'register' | 'preferences';
  showHeaderAndFooter?: boolean;
  transparentBackgroundDarkMode?: boolean;
}

const PagesNewsletterPreferences: React.FunctionComponent<PagesNewsletterPreferencesProps> = ({
  variant = 'preferences',
  showHeaderAndFooter = true,
  transparentBackgroundDarkMode,
}) => {
  const intl = useIntl();
  const { error: errorRedirect, _cid, _email } = querystring.parse(getCurrentUrlParams());
  const [error, setError] = useState('');
  const [submitSucess, setSubmitSucess] = useState(false);
  const [unsubscribe, setUnsibscribe] = useState(false);
  const [formData, setFormData] = useState(null);
  const { outputChannel } = useLocale();
  const pageProperties = {
    title:
      variant === 'preferences'
        ? intl.formatMessage({ id: 'page/newsletter/preferences/head/title' })
        : intl.formatMessage({ id: 'page/newsletterForm/title' }),
    description:
      variant === 'preferences'
        ? intl.formatMessage({ id: 'page/newsletter/preferences/head/description' })
        : '',
    showBreadcrumbs: false,
    topElement: null,
    noIndex: true,
  };

  const newsletterApiUrl =
    process.env.NEXT_PUBLIC_NEWSLETTER_API_URL || 'https://cloud.news.walterknoll.de';

  const resetMessages = () => {
    setError(null);
    setSubmitSucess(false);
    setUnsibscribe(false);
  };

  useEffect(() => {
    if (typeof document === 'object' && transparentBackgroundDarkMode) {
      document.body.classList.add('transparent-background-dark-mode');

      return () => {
        document.body.classList.remove('transparent-background-dark-mode');
      };
    }

    return undefined;
  }, [transparentBackgroundDarkMode]);

  const onFinishPreferences = async (values) => {
    try {
      resetMessages();
      const response = await axios.post(
        `${newsletterApiUrl}/api_pref_center_patch`,
        querystring.stringify({
          ...values,
          ...getNewsletterValues(values),
          ...{ _cid, _email, _objectType: formData._objectType },
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (response.data?.Errors?.length) {
        setError(
          `${intl.formatMessage({ id: 'form/errorRegistration' })}: ${response.data?.Errors.join(
            '. '
          )}`
        );
      } else if (response.data?.Errors?.NL_Source_Website) {
        setError(
          `${intl.formatMessage({
            id: 'form/errorRegistration',
          })}: ${response.data?.Errors?.NL_Source_Website.join('. ')}`
        );
      } else {
        setSubmitSucess(true);
      }
    } catch (error) {
      // setSubmitSucess(true);
      setError(intl.formatMessage({ id: 'form/errorRegistration' }));
      setUnsibscribe(false);
      error && console.error(error);
    }
  };

  const onUnsubscribe = async () => {
    try {
      resetMessages();
      const response = await axios.post(
        `${newsletterApiUrl}/ctrl_unsubscribe`,
        querystring.stringify({ _cid, _email, _objectType: formData._objectType }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (response.data?.Errors?.length) {
        setError(
          `${intl.formatMessage({ id: 'form/errorRegistration' })}: ${response.data?.Errors.join(
            '. '
          )}`
        );
      } else {
        setUnsibscribe(true);
      }
    } catch (error) {
      // setSubmitSucess(true);
      setError(intl.formatMessage({ id: 'form/errorRegistration' }));
      setUnsibscribe(false);
      error && console.error(error);
    }
  };

  useEffect(() => {
    if (variant !== 'preferences') return;

    const fetchData = async () => {
      try {
        const response = await axios.get(`${newsletterApiUrl}/api_pref_center_get`, {
          params: { ...{ _cid, _email } },
        });
        if (response.data?.Errors?.length) {
          setError(
            `${intl.formatMessage({
              id: 'form/newsletter/errorFetchPreferenceCenter',
            })}: ${response.data?.Errors.join('. ')}`
          );
        } else {
          setFormData(response.data.model);
        }
      } catch (error) {
        console.log(error);
        // setFormData({
        //   _objectType: 'V3ZRdEFDaExPdzFUdzNtdU5XQ3V5dz09',
        //   Salutation: '',
        //   Interest: 'Private',
        //   BusinessField: '',
        //   FirstName: '',
        //   LastName: 'Unknown',
        //   Language: 'English',
        //   Country: 'Tuvalu',
        //   NL_Source_Website: 'www.walter-k.com',
        // });
      }
    };
    !!!formData && fetchData();
  }, [newsletterApiUrl, formData, _cid, _email, intl, variant]);

  //Check if there is an error coming from the redirect of the newsletter site
  if (variant === 'preferences' && errorRedirect) {
    return <PagesNewsletter newsletterPage={NewsletterPageTypes.Preferences} />;
  }

  //Check if it's been sucessfully unsubscribed
  if (variant === 'preferences' && unsubscribe) {
    return <PagesNewsletter newsletterPage={NewsletterPageTypes.Unsubscribe} />;
  }

  if (error) {
    pageProperties.topElement = (
      <div className={styles.error}>
        {intl.formatMessage({ id: 'page/newsletter/preferences/form/error' })}
      </div>
    );
  }
  if (submitSucess) {
    // TODO different message for variant
    pageProperties.topElement = (
      <div className={styles.success}>
        {variant === 'preferences'
          ? intl.formatMessage({ id: 'page/newsletter/preferences/form/success' })
          : intl.formatMessage({ id: 'form/confirmationSubline' })}
      </div>
    );
  }

  const Wrapper: FC = ({ children }) => {
    if (showHeaderAndFooter) return <LayoutPage {...pageProperties}>{children}</LayoutPage>;

    return (
      <div>
        <Head>
          <title>
            {`${pageProperties.title} | ${
              outputChannel === OutputChannels.US ? 'Walter K.' : 'Walter Knoll'
            }`}
          </title>
        </Head>
        <div className={styles.noHeaderFooterErrorContainer}>{pageProperties.topElement}</div>
        <div className={styles.noHeaderFooterContainer}>{children}</div>
      </div>
    );
  };

  return (
    <Wrapper>
      {/* {!!error && (
        <div className={styles.error}>
          {intl.formatMessage({ id: 'page/newsletter/preferences/form/error' })}
        </div>
      )}
      {!!submitSucess && (
        <div className={styles.success}>
          {intl.formatMessage({ id: 'page/newsletter/preferences/form/success' })}
        </div>
      )} */}
      <GlobalFormNewsletter
        onFinish={
          variant === 'preferences'
            ? onFinishPreferences
            : (values: any) => onFinishGlobalNewsletter({ values, setError, intl, setSubmitSucess })
        }
        buttonLabel={
          variant === 'preferences'
            ? intl.formatMessage({ id: 'page/newsletter/preferences/buttonSave' })
            : undefined /* use default */
        }
        data={formData}
        type={
          variant === 'preferences' ? NewsletterPageTypes.Preferences : NewsletterPageTypes.Register
        }
      />
      {variant === 'preferences' && (
        <>
          <div className={styles.separator} />
          <Row {...rowDefaultProps}>
            <Col xs={{ span: 24, offset: 0 }} md={{ span: 6, offset: 6 }}>
              <GlobalButton
                type="secondary"
                widthType="fullWidth"
                onClick={onUnsubscribe}
                className={styles.subscribeButton}>
                {intl.formatMessage({ id: 'page/newsletter/preferences/buttonUnsubscribe' })}
              </GlobalButton>
            </Col>
          </Row>
        </>
      )}
    </Wrapper>
  );
};

export default PagesNewsletterPreferences;
